import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import DeleteIcon from "@mui/icons-material/Delete";
import TimerIcon from "@mui/icons-material/Timer";
import {
  useChatMessagesQuery,
  useCreateChatBanMutation,
  useDeleteChatMessageMutation,
} from "@/partner/entities/chat";
import { useFilter } from "@/partner/shared/components/Filter";
import { EmptyListCard } from "@/partner/shared/components/EmptyListCard";
import { interpolationFormat } from "@/partner/shared/lib";
import { ConfirmDialog, ErrorMessage } from "@/shared/components";
import {
  ChatBanTypeType,
  ChatMessageTypeType,
} from "@/types/graphql/partner.generated";
import type { ChatMessagesQueryType } from "@/types/graphql/partner.generated";
import { buildChatMessagesFilter } from "../model/buildChatMessagesFilter";

type MessageRow = ChatMessagesQueryType["chatMessages"]["nodes"][number];

/**
 * Удаление сообщения и бан необратимы, поэтому оба действия идут через
 * подтверждение: в заголовке диалога подпись действия, в теле — то, что
 * пострадает (текст сообщения или имя автора).
 */
type ConfirmAction =
  | { kind: "delete"; row: MessageRow }
  | { kind: "ban"; row: MessageRow; type: ChatBanTypeType };

// Имени у автора может не быть (снэпшот пустой, пользователь не подгрузился),
// поэтому в хвосте — короткий хвост id: ячейка не должна оставаться пустой,
// иначе модератор не отличит одну строку от другой.
const shortUserId = (userId: string): string => `ID ${userId.slice(-6)}`;

export const ChatMessagesList: FC = () => {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const {
    filter,
    onPageChange,
    onRowsPerPageChange,
    params: { page, rowsPerPage },
  } = useFilter();
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(
    null
  );

  const { data, loading, error, refetch } = useChatMessagesQuery({
    variables: { page, rowsPerPage, filter: buildChatMessagesFilter(filter) },
    fetchPolicy: "cache-and-network",
  });

  const [deleteChatMessage, { loading: deleting }] =
    useDeleteChatMessageMutation({
      onCompleted: () => {
        enqueueSnackbar(t("chat.list.deletedSnackbar"), { variant: "success" });
        void refetch();
      },
      onError: (mutationError) =>
        enqueueSnackbar(mutationError.message, { variant: "error" }),
    });

  const [createChatBan, { loading: banning }] = useCreateChatBanMutation({
    onCompleted: () => {
      enqueueSnackbar(t("chat.list.bannedSnackbar"), { variant: "success" });
    },
    onError: (mutationError) =>
      enqueueSnackbar(mutationError.message, { variant: "error" }),
  });

  const rows = data?.chatMessages?.nodes ?? [];
  const count = data?.chatMessages?.count ?? 0;

  const authorName = (row: MessageRow): string =>
    row.userDisplayName ??
    row.user?.displayName ??
    row.user?.email ??
    (row.userId ? shortUserId(row.userId) : t("chat.list.unknownAuthor"));

  const handleConfirm = async () => {
    if (!confirmAction) return;

    if (confirmAction.kind === "delete") {
      await deleteChatMessage({ variables: { id: confirmAction.row.id } });
    } else if (confirmAction.row.userId) {
      await createChatBan({
        variables: {
          input: {
            userId: confirmAction.row.userId,
            type: confirmAction.type,
          },
        },
      });
    }

    setConfirmAction(null);
  };

  const confirmTitle = !confirmAction
    ? ""
    : confirmAction.kind === "delete"
      ? t("chat.list.deleteAction")
      : confirmAction.type === ChatBanTypeType.TemporaryType
        ? t("chat.list.banTemporaryAction")
        : t("chat.list.banPermanentAction");

  const confirmText = !confirmAction
    ? ""
    : confirmAction.kind === "delete"
      ? (confirmAction.row.message ?? "")
      : authorName(confirmAction.row);

  if (error) return <ErrorMessage error={error} />;

  if (loading && !data) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 300,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (count === 0) return <EmptyListCard title={t("chat.list.empty")} />;

  return (
    <Card elevation={0} sx={{ overflowX: "auto" }}>
      <Table size="medium">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 180 }}>
              {t("chat.list.timeColumn")}
            </TableCell>
            <TableCell sx={{ width: 200 }}>
              {t("chat.list.authorColumn")}
            </TableCell>
            <TableCell sx={{ width: 240 }}>
              {t("chat.list.contentColumn")}
            </TableCell>
            <TableCell>{t("chat.list.messageColumn")}</TableCell>
            <TableCell align="right" sx={{ width: 160 }}>
              {t("chat.list.actionsColumn")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            // Системные сообщения (вход в эфир и т.п.) пишет не зритель:
            // банить там некого, поэтому автор помечен чипом, а кнопки бана
            // выключены (у таких сообщений нет userId).
            const isSystem = row.messageType === ChatMessageTypeType.SystemType;

            return (
              <TableRow key={row.id} hover>
                <TableCell>
                  {interpolationFormat(
                    row.createdAt,
                    "dateTime",
                    i18n.language
                  )}
                </TableCell>
                <TableCell>
                  {isSystem ? (
                    <Chip size="small" label={t("chat.list.systemAuthor")} />
                  ) : (
                    authorName(row)
                  )}
                </TableCell>
                <TableCell>{row.content?.title ?? ""}</TableCell>
                <TableCell>{row.message}</TableCell>
                <TableCell align="right">
                  <Stack
                    direction="row"
                    spacing={0.5}
                    justifyContent="flex-end"
                  >
                    <Tooltip title={t("chat.list.deleteAction")}>
                      <IconButton
                        size="small"
                        onClick={() =>
                          setConfirmAction({ kind: "delete", row })
                        }
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("chat.list.banTemporaryAction")}>
                      {/* span: у выключенной кнопки нет событий мыши, без
                          обёртки Tooltip на ней не показывается. */}
                      <span>
                        <IconButton
                          size="small"
                          disabled={!row.userId}
                          onClick={() =>
                            setConfirmAction({
                              kind: "ban",
                              row,
                              type: ChatBanTypeType.TemporaryType,
                            })
                          }
                        >
                          <TimerIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t("chat.list.banPermanentAction")}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={!row.userId}
                          onClick={() =>
                            setConfirmAction({
                              kind: "ban",
                              row,
                              type: ChatBanTypeType.PermanentType,
                            })
                          }
                        >
                          <BlockIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <ConfirmDialog
        open={Boolean(confirmAction)}
        loading={deleting || banning}
        confirmTitle={confirmTitle}
        confirmText={confirmText}
        onCancel={() => setConfirmAction(null)}
        onConfirm={() => void handleConfirm()}
      />

      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[5, 10, 25, 100]}
        labelRowsPerPage={t("common.pagination.rowsPerPage")}
        labelDisplayedRows={({ from, to, count: total }) =>
          t("common.pagination.displayedRows", { from, to, count: total })
        }
      />
    </Card>
  );
};
